import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';

export interface SidebarItem {
	icon?: React.ReactNode;
	text: string;
	path: string;
	type?: 'link' | 'section';
}

export type TutorialNavHighlightRect = {
	top: number;
	left: number;
	right: number;
	bottom: number;
};

interface SidebarContextData {
	isExpanded: boolean;
	toggleSidebar: () => void;
	closeSidebar: () => void;
	isMobile: boolean;
	/** Tutorial: passo 1 ou 2 — desktop expande a rail; mobile abre o drawer. */
	tutorialForceSidebarOpen: boolean;
	/** Passo 2: destaca só a lista Início / Perfil / IPBE / Clubes. */
	tutorialHighlightNavItemsOnly: boolean;
	/** Retângulo em coords de viewport para o “buraco” do overlay (passo 2). */
	tutorialNavHighlightRect: TutorialNavHighlightRect | null;
	setTutorialNavHighlightRect: (r: TutorialNavHighlightRect | null) => void;
	items: SidebarItem[];
	handleLogout: () => void;
	/** Quando informado, o avatar no rodapé vira atalho para o perfil. */
	profilePath?: string;
	/** Exibe avatar e dados do usuário no rodapé da sidebar. */
	showProfileFooter?: boolean;
	/** Exibe o botão Sair no rodapé da sidebar. */
	showSidebarLogout?: boolean;
	/** Rail mais estreita, itens e espaçamentos menores (ex.: admin). */
	compact?: boolean;
}

const SidebarContext = createContext<SidebarContextData | null>(null);

interface SidebarProviderProps {
	children: React.ReactNode;
	items: SidebarItem[];
	onLogout: () => void;
	profilePath?: string;
	showProfileFooter?: boolean;
	showSidebarLogout?: boolean;
	tutorialForceSidebarOpen?: boolean;
	/** Passo 2 do tour (atalhos da sidebar). */
	tutorialHighlightNavItemsOnly?: boolean;
	compact?: boolean;
}

export const SidebarProvider = ({
	children,
	items,
	onLogout,
	profilePath,
	showProfileFooter = true,
	showSidebarLogout = true,
	tutorialForceSidebarOpen = false,
	tutorialHighlightNavItemsOnly = false,
	compact = false,
}: SidebarProviderProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isMobile, setIsMobile] = useState(() =>
		typeof window !== 'undefined' ? window.innerWidth <= 1024 : false,
	);
	const tutorialForceRef = useRef(tutorialForceSidebarOpen);
	tutorialForceRef.current = tutorialForceSidebarOpen;

	const [tutorialNavHighlightRect, setTutorialNavHighlightRect] =
		useState<TutorialNavHighlightRect | null>(null);

	const setTutorialNavHighlightRectStable = useCallback(
		(r: TutorialNavHighlightRect | null) => {
			setTutorialNavHighlightRect(r);
		},
		[],
	);

	useEffect(() => {
		if (!tutorialHighlightNavItemsOnly) {
			setTutorialNavHighlightRect(null);
		}
	}, [tutorialHighlightNavItemsOnly]);

	const toggleSidebar = () => setIsExpanded((prev) => !prev);

	const closeSidebar = useCallback(() => {
		if (!tutorialForceRef.current) {
			setIsExpanded(false);
		}
	}, []);

	useEffect(() => {
		const handleResize = () => {
			const mobile = window.innerWidth <= 1024;
			setIsMobile(mobile);
			if (mobile && !tutorialForceRef.current) {
				setIsExpanded(false);
			}
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	/** Tour passos sidebar / sidebar_nav: abre rail (desktop) ou drawer (mobile). */
	useEffect(() => {
		if (tutorialForceSidebarOpen) {
			setIsExpanded(true);
		} else if (isMobile) {
			setIsExpanded(false);
		}
	}, [tutorialForceSidebarOpen, isMobile]);

	return (
		<SidebarContext.Provider
			value={{
				isExpanded,
				toggleSidebar,
				closeSidebar,
				isMobile,
				tutorialForceSidebarOpen,
				tutorialHighlightNavItemsOnly,
				tutorialNavHighlightRect,
				setTutorialNavHighlightRect: setTutorialNavHighlightRectStable,
				items,
				handleLogout: onLogout,
				profilePath,
				showProfileFooter,
				showSidebarLogout,
				compact,
			}}
		>
			{children}
		</SidebarContext.Provider>
	);
};

export const useSidebar = () => {
	const context = useContext(SidebarContext);
	if (!context) {
		return {
			isExpanded: false,
			toggleSidebar: () => {},
			closeSidebar: () => {},
			isMobile: false,
			tutorialForceSidebarOpen: false,
			tutorialHighlightNavItemsOnly: false,
			tutorialNavHighlightRect: null,
			setTutorialNavHighlightRect: () => {},
			items: [],
			handleLogout: () => {},
			profilePath: undefined,
			showProfileFooter: true,
			showSidebarLogout: true,
			compact: false,
		};
	}
	return context;
};
